import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from '../../shared/ss-select-tooltip.directive';

import { DvmBuilderStepBase } from '../../dvm-layout-builder/dvm-builder-host';
import type { DashboardTile } from '../../dvm-layout-builder/dvm-layout-builder.types';
import { SelfServiceUiService } from '../../shared/self-service-ui.service';

/**
 * File Upload build form (FILE_UPLOAD): a standalone uploader tile whose
 * picked file POSTs to the compute API and re-renders the selected target
 * chart(s) / table(s). Display-only at build time — no compute call.
 */
@Component({
  selector: 'app-file-upload-step',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, SsSelectTooltipDirective],
  templateUrl: './file-upload-step.component.html',
  styles: [':host { display: contents; }'],
})
export class FileUploadStepComponent extends DvmBuilderStepBase {
  readonly layoutTypes = ['FILE_UPLOAD'] as const;
  private readonly ui = inject(SelfServiceUiService);

  fileUploadButtonLabel = 'Upload file';
  fileUploadAccept = '.xlsx,.xls,.csv';

  // ── DvmBuilderStep ──────────────────────────────────────────────────────

  blockers(): string[] {
    return this.host.selectedTargetCharts.length ? [] : ['At least one target chart'];
  }

  buildPayload(): Record<string, unknown> {
    return {
      componentId: this.host.componentId,
      dashboardId: this.host.dashboardId,
      componentType: 'INPUT',
      inputType: 'FILE_UPLOAD',
      title: this.host.title.trim(),
      buttonLabel: this.fileUploadButtonLabel.trim() || 'Upload file',
      accept: this.fileUploadAccept.trim() || '.xlsx,.xls,.csv',
      value: '',
      defaultValue: '',
      targets: this.host.selectedTargetCharts.map((componentId) => ({
        componentId,
        label: this.host.targetLabelForId(componentId),
      })),
      ...this.host.apiCallConfigPayload(),
    };
  }

  loadFromTile(_tile: DashboardTile, payload: Record<string, unknown>): void {
    this.fileUploadButtonLabel = String(payload['buttonLabel'] ?? 'Upload file');
    this.fileUploadAccept = String(payload['accept'] ?? '.xlsx,.xls,.csv');
    const targets = Array.isArray(payload['targets']) ? payload['targets'] as Array<Record<string, unknown>> : [];
    this.host.selectedTargetCharts = targets.map((target) => String(target['componentId'] ?? '')).filter(Boolean);
  }

  reset(): void {
    this.fileUploadButtonLabel = 'Upload file';
    this.fileUploadAccept = '.xlsx,.xls,.csv';
  }

  /** No compute call: the tile is added straight to the canvas. */
  generate(payload: Record<string, unknown>): boolean {
    this.host.isGenerating = false;
    const updated = this.host.addTile(this.buildTile(payload));
    this.ui.showSuccess(updated ? 'File upload updated on the dashboard.' : 'File upload added to the dashboard.');
    return true;
  }

  private buildTile(payload: Record<string, unknown>): DashboardTile {
    const base = this.host.newTileBase(payload);
    const buttonLabel = this.fileUploadButtonLabel.trim() || 'Upload file';
    const accept = this.fileUploadAccept.trim() || '.xlsx,.xls,.csv';
    return {
      ...base,
      colSpan: 4,
      rowSpan: 4,
      fileUploadControl: {
        targets: [...this.host.selectedTargetCharts],
        buttonLabel,
        accept,
        apiConfig: { ...this.host.buildApiCallConfig },
        widgetData: {
          title: this.host.title.trim() || 'File Upload',
          buttonLabel,
          accept,
          targets: this.host.selectedTargetCharts.map((componentId) => ({
            componentId,
            label: this.host.targetLabelForId(componentId),
          })),
        },
      },
    };
  }
}
